import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Toast } from '@jpmuitk/toast';
import { useTranslation } from 'react-i18next';
import { acknowledgeEntityUpload } from 'routes/Entity/actions';

/**
 * Headless component that raises background entity-upload toast notifications from Redux state.
 *
 * It is mounted once at the app layout level (MainLayout) rather than inside the UploadEntity
 * route so that async upload notifications continue to fire regardless of which screen the user
 * has navigated to while the background job is processing.
 */
const AsyncUploadToasts = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const saveSuccessToast = useSelector(state => state.entity?.saveSuccessToast);
  const isSaveFail = useSelector(state => state.entity?.isSaveFail);
  const uploadErrors = useSelector(state => state.entity?.uploadErrors);
  const asyncUploadJobId = useSelector(state => state.entity?.asyncUploadJobId);
  const asyncUploadSubmitted = useSelector(state => state.entity?.asyncUploadSubmitted);
  const asyncUploadDuplicate = useSelector(state => state.entity?.asyncUploadDuplicate);

  // Success: the background job completed. Acknowledge (clean up) the job only once the user
  // dismisses the toast, not while it is still displayed. A ref guards against the same success
  // toast being raised again on unrelated re-renders while saveSuccessToast is still truthy.
  const lastShownSuccessRef = useRef(null);
  useEffect(() => {
    if (!saveSuccessToast?.message || lastShownSuccessRef.current === saveSuccessToast) {
      return;
    }
    lastShownSuccessRef.current = saveSuccessToast;
    toast.success(({ closeToast }) => (
      <Toast
        state="success"
        title={t('uploadentity.toastTitle')}
        onClose={() => {
          if (asyncUploadJobId) {
            dispatch(acknowledgeEntityUpload(asyncUploadJobId));
          } else {
            // The completed job cannot be acknowledged/cleaned up without its id. Surface this for
            // observability instead of silently skipping the cleanup call.
            // eslint-disable-next-line no-console
            console.warn('AsyncUploadToasts: missing asyncUploadJobId on success dismissal; skipping acknowledge cleanup.');
          }
          closeToast();
        }}
      >
        {saveSuccessToast.message}
      </Toast>
    ));
  }, [saveSuccessToast]);

  // Failure: the background job failed (or the submit/poll request errored).
  useEffect(() => {
    if (isSaveFail && uploadErrors?.message) {
      toast.error(({ closeToast }) => (
        <Toast state="error" title={t('uploadentity.async.failureToastTitle')} onClose={closeToast}>
          {uploadErrors.message}
        </Toast>
      ));
    }
  }, [isSaveFail, uploadErrors?.message]);

  // Submitted: a large upload was accepted for background processing.
  useEffect(() => {
    if (asyncUploadSubmitted) {
      toast.info(({ closeToast }) => (
        <Toast state="info" title={t('uploadentity.async.submittedToastTitle')} onClose={closeToast}>
          {t('uploadentity.async.submitted')}
        </Toast>
      ));
    }
  }, [asyncUploadSubmitted]);

  // In progress: a prior upload is still running (duplicate submission rejected with 409).
  useEffect(() => {
    if (asyncUploadDuplicate) {
      toast.warning(({ closeToast }) => (
        <Toast state="warning" title={t('uploadentity.async.inProgressToastTitle')} onClose={closeToast}>
          {uploadErrors?.message || t('uploadentity.async.inProgress')}
        </Toast>
      ));
    }
  }, [asyncUploadDuplicate]);

  return null;
};

export default AsyncUploadToasts;

